import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Terminal, Printer, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";

// Phase 25.14c — Regulator Pack preview.
// Standalone, print-ready page that prospects (utility CISOs, auditors,
// procurement) can open from /industrial-demo and screenshot or print to
// PDF. The content is illustrative — every page makes that explicit — but
// it gives a CISO something concrete to react to instead of marketing copy.

const ACCENT = "#FFB000";

const SECTIONS = [
  {
    code: "IEC 62443-3-3",
    label: "System Security Requirements (SR)",
    items: [
      { sr: "SR 1 · Identification & authentication control", coverage: "Aligned", notes: "Per-tenant OAuth + SSO; OPC UA cert-based auth on connector reads." },
      { sr: "SR 2 · Use control", coverage: "Aligned", notes: "Read-only client. No write methods exposed in DARA connector binary." },
      { sr: "SR 3 · System integrity", coverage: "Aligned", notes: "Signed connector releases. SBOM published per release. No code-loading in the field." },
      { sr: "SR 5 · Restricted data flow", coverage: "Aligned", notes: "Egress-only TLS 1.3. No inbound. Workspace-isolated storage." },
      { sr: "SR 7 · Resource availability", coverage: "Aligned", notes: "Connector rate-limited to ≤ 60 reads/min/tag. No PLC scan-cycle disruption observed in any pilot." },
    ],
  },
  {
    code: "NIST CSF 2.0",
    label: "Identify · Protect · Detect · Respond · Recover",
    items: [
      { sr: "ID.AM-1 · Asset inventory", coverage: "Aligned", notes: "Every connected tag, asset, and historian source is inventoried in the workspace." },
      { sr: "PR.AC-1 · Access control", coverage: "Aligned", notes: "Per-user, per-workspace RBAC. SOC 2 control alignment in progress." },
      { sr: "DE.AE · Anomalies and events", coverage: "Aligned", notes: "Each anomaly graded NOMINAL / DRIFT / ANOMALY with audit-trail." },
      { sr: "DE.CM · Continuous monitoring", coverage: "Aligned", notes: "30-60s read cadence. Daily reconciled brief at 06:00 local." },
      { sr: "RS.AN · Analysis", coverage: "Aligned", notes: "Root-cause hypothesis generated per anomaly with action recommendation." },
    ],
  },
  {
    code: "AS/NZS 4360 · ISO 31000",
    label: "Risk management framework",
    items: [
      { sr: "Risk identification", coverage: "Aligned", notes: "Anomalies auto-logged to the tenant's risk register." },
      { sr: "Risk analysis", coverage: "Aligned", notes: "Consequence × likelihood × residual rating applied per anomaly class." },
      { sr: "Risk treatment", coverage: "Aligned", notes: "Each anomaly carries a recommended action and owner." },
      { sr: "Monitoring & review", coverage: "Aligned", notes: "Weekly trend report; monthly risk-register review packet." },
    ],
  },
  {
    code: "ACSC ISM (Australia)",
    label: "Australian Government Information Security Manual",
    items: [
      { sr: "Control 1735 · OT read-only telemetry", coverage: "Compliant", notes: "Read-only by design. No actuation, no writes, no PLC firmware touch." },
      { sr: "Control 0072 · Data sovereignty", coverage: "Compliant", notes: "AU-sovereign storage. No cross-border data transfer without explicit tenant authorisation." },
      { sr: "Control 1546 · Cryptography in transit", coverage: "Compliant", notes: "TLS 1.3 outbound only. AES-256 at rest." },
    ],
  },
  {
    code: "WSAA G2 · Water Services Association of Australia",
    label: "Cyber security for water utilities",
    items: [
      { sr: "G2 · Section 4 · Network segmentation", coverage: "Roadmap", notes: "Mapping finalised with utility CISO during pilot weeks 2-3." },
      { sr: "G2 · Section 7 · Supply chain risk", coverage: "Roadmap", notes: "SBOM + dependency mapping per release; tenant sign-off pre-deployment." },
    ],
  },
];

const RISK_REGISTER = [
  { id: "RR-001", asset: "Booster 2 · Northam", consequence: "High", likelihood: "Medium", residual: "Medium", flagged_by: "DARA · cavitation signature 2026-05-22" },
  { id: "RR-002", asset: "Rising-main AV-RM-4B → AV-RM-4D", consequence: "High", likelihood: "Medium", residual: "Medium", flagged_by: "DARA · mass-balance gap 2026-05-21" },
  { id: "RR-003", asset: "Chlorine dosing pump 1", consequence: "High", likelihood: "Low", residual: "Low-Med", flagged_by: "DARA · under-injection 2026-05-20" },
  { id: "RR-004", asset: "Aeration blower 1 · diffuser fouling", consequence: "Medium", likelihood: "High", residual: "Medium", flagged_by: "DARA · energy creep 2026-05-19" },
];

export default function RegulatorPackPreview() {
  const now = useMemo(() => new Date(), []);
  const generated = now.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  const buildId = useMemo(
    () => `RP-${now.toISOString().slice(0, 10).replace(/-/g, "")}-${Math.floor(Math.random() * 9000 + 1000)}`,
    [now],
  );
  const [printing, setPrinting] = useState(false);

  // When the user hits Print, swap to a white-paper layout briefly so the
  // browser's "Save as PDF" produces a clean, readable document.
  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrinting(false), 200);
    }, 100);
  };

  useEffect(() => {
    document.title = "DARA · Regulator Pack Preview";
  }, []);

  return (
    <div className={printing ? "min-h-screen bg-white text-black" : "min-h-screen bg-black text-white"} data-testid="regulator-pack-preview">
      <header className={`border-b ${printing ? "border-zinc-300" : "border-zinc-900"} sticky top-0 z-10 ${printing ? "bg-white" : "bg-black/95 backdrop-blur"} print:hidden`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/industrial-demo" className="flex items-center gap-2">
            <Terminal className="w-5 h-5" style={{ color: ACCENT }} />
            <span className="font-heading font-black tracking-tight">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hidden sm:inline">← BACK TO DEMO</span>
          </Link>
          <button
            onClick={handlePrint}
            data-testid="regulator-pack-print"
            className="border border-zinc-700 hover:border-white text-white font-mono text-[11px] uppercase tracking-widest px-4 py-2 flex items-center gap-2"
          >
            <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
          </button>
        </div>
      </header>

      <article className={`max-w-5xl mx-auto px-4 sm:px-6 py-10 ${printing ? "print:py-6" : ""}`}>
        {/* Cover */}
        <div className={`border-b ${printing ? "border-zinc-300" : "border-zinc-800"} pb-8 mb-10`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: ACCENT }}>
            // REGULATOR PACK · CONTROL FRAMEWORK ALIGNMENT
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tight leading-[1.05]">
            DARA · Read-only Industrial Intelligence
          </h1>
          <h2 className="text-lg sm:text-xl mt-3 opacity-80">
            Control-framework alignment pack — illustrative artefact for utility CISOs and auditors
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 font-mono text-[11px]">
            <Meta label="Generated" value={generated} />
            <Meta label="Pack ID" value={buildId} />
            <Meta label="Site profile" value="Water utility · Tier-2" />
            <Meta label="Status" value="Illustrative · pre-pilot" accent={ACCENT} />
          </div>
        </div>

        {/* Honest preamble */}
        <div className={`border ${printing ? "border-zinc-300 bg-zinc-50" : "border-[#FFB000]/40 bg-[#FFB000]/[0.04]"} p-5 mb-10`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ACCENT }} />
            <p className="text-sm leading-relaxed">
              <strong>This is not a compliance certification.</strong> DARA is a read-only
              industrial intelligence layer — every anomaly it surfaces is tagged to the control
              framework your regulator cares about, so your CISO can hand the artefacts straight to
              the auditor. The mappings below are illustrative until finalised with your CISO and
              named auditor during pilot weeks 2-3.
            </p>
          </div>
        </div>

        {/* Sections */}
        {SECTIONS.map(s => (
          <section key={s.code} className="mb-10" data-testid={`pack-section-${s.code.replace(/[\s/.]/g, "-")}`}>
            <div className="flex items-baseline justify-between gap-4 mb-3 flex-wrap">
              <h3 className="font-heading font-bold text-xl tracking-tight">{s.code}</h3>
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">{s.label}</div>
            </div>
            <div className={`border ${printing ? "border-zinc-300" : "border-zinc-900"} overflow-x-auto`}>
              <table className="w-full font-mono text-[11px]">
                <thead className={printing ? "bg-zinc-100" : "bg-zinc-950"}>
                  <tr className={`uppercase tracking-widest text-[10px] ${printing ? "text-zinc-600" : "text-zinc-500"}`}>
                    <th className={`text-left px-4 py-2.5 border-b ${printing ? "border-zinc-300" : "border-zinc-900"}`}>Control</th>
                    <th className={`text-left px-4 py-2.5 border-b ${printing ? "border-zinc-300" : "border-zinc-900"}`}>Coverage</th>
                    <th className={`text-left px-4 py-2.5 border-b ${printing ? "border-zinc-300" : "border-zinc-900"}`}>Mapping notes</th>
                  </tr>
                </thead>
                <tbody>
                  {s.items.map((it, i) => (
                    <tr key={i} className={i % 2 ? (printing ? "bg-white" : "bg-black") : (printing ? "bg-zinc-50" : "bg-zinc-950/40")}>
                      <td className={`px-4 py-3 ${printing ? "text-black" : "text-white"}`}>{it.sr}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 ${it.coverage === "Roadmap" ? (printing ? "text-amber-700" : "text-[#FFB000]") : (printing ? "text-emerald-700" : "text-[#00E676]")}`}>
                          {it.coverage === "Roadmap" ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                          {it.coverage}
                        </span>
                      </td>
                      <td className={`px-4 py-3 leading-snug ${printing ? "text-zinc-700" : "text-zinc-400"}`}>{it.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}

        {/* Live risk-register snapshot */}
        <section className="mb-10" data-testid="pack-risk-register">
          <div className="flex items-baseline gap-3 mb-3">
            <ShieldCheck className="w-5 h-5" style={{ color: ACCENT }} />
            <h3 className="font-heading font-bold text-xl tracking-tight">Risk register · live snapshot</h3>
          </div>
          <p className="text-sm leading-relaxed opacity-80 mb-4 max-w-3xl">
            Every anomaly DARA flags is auto-logged to your risk register with consequence,
            likelihood, and residual rating — auditable, time-stamped, and exportable.
          </p>
          <div className={`border ${printing ? "border-zinc-300" : "border-zinc-900"} overflow-x-auto`}>
            <table className="w-full font-mono text-[11px]">
              <thead className={printing ? "bg-zinc-100" : "bg-zinc-950"}>
                <tr className={`uppercase tracking-widest text-[10px] ${printing ? "text-zinc-600" : "text-zinc-500"}`}>
                  <th className="text-left px-4 py-2.5">ID</th>
                  <th className="text-left px-4 py-2.5">Asset</th>
                  <th className="text-left px-4 py-2.5">Consequence</th>
                  <th className="text-left px-4 py-2.5">Likelihood</th>
                  <th className="text-left px-4 py-2.5">Residual</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">Flagged by</th>
                </tr>
              </thead>
              <tbody>
                {RISK_REGISTER.map((r, i) => (
                  <tr key={r.id} className={i % 2 ? (printing ? "bg-white" : "bg-black") : (printing ? "bg-zinc-50" : "bg-zinc-950/40")}>
                    <td className="px-4 py-3">{r.id}</td>
                    <td className={`px-4 py-3 ${printing ? "text-black" : "text-white"}`}>{r.asset}</td>
                    <td className="px-4 py-3">{r.consequence}</td>
                    <td className="px-4 py-3">{r.likelihood}</td>
                    <td className="px-4 py-3">{r.residual}</td>
                    <td className={`px-4 py-3 leading-snug hidden md:table-cell ${printing ? "text-zinc-700" : "text-zinc-400"}`}>{r.flagged_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Footer */}
        <div className={`border-t ${printing ? "border-zinc-300" : "border-zinc-800"} pt-6 mt-12 flex items-center justify-between flex-wrap gap-3`}>
          <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">
            DARA · {generated} · {buildId} · Illustrative · Page 1 of 1
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">
            daraos.ai · regulator pack preview
          </div>
        </div>
      </article>
    </div>
  );
}

function Meta({ label, value, accent }) {
  return (
    <div>
      <div className="opacity-50 text-[9px] uppercase tracking-widest">{label}</div>
      <div className="mt-1" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}
